import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  Plus, Pencil, Search, X, Loader2, Scale, Receipt, Package, Boxes, Trash2,
  Lightbulb, ImageIcon, Star, StarOff, Upload, Sparkles, Tag, QrCode, Printer,
  ChevronLeft, ChevronRight, Copy, Store,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import FilialLiberacoes from '@/components/FilialLiberacoes'
import UltimosCadastros from '@/components/UltimosCadastros'
import { useAuth } from '@/lib/auth'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

// Empresas habilitadas pra grade de borracha (cor/espessura/etc.).
// Mantém em sync com EMPRESAS_COM_BORRACHA do backend.
const EMPRESAS_COM_GRADES = new Set([11])

interface Produto {
  id?: number; codigo: string; ean?: string; descricao: string; marca?: string
  categoria_id?: number; conta_gerencial_id?: number | null
  unidade: string; preco_custo: number; preco_venda: number
  estoque_atual: number; estoque_consolidado?: number; estoque_minimo: number
  endereco_estoque?: string; saida_por?: string
  ncm?: string; cfop?: string; cst?: string; origem: string
  cest?: string; csosn?: string
  cst_pis?: string; cst_cofins?: string
  aliquota_icms?: number | string; aliquota_pis?: number | string; aliquota_cofins?: number | string
  cst_ipi?: string; aliquota_ipi?: number | string; codigo_enquadramento_ipi?: string
  unidade_tributavel?: string; quantidade_tributavel?: number | string; gtin_tributavel?: string
  peso_liquido?: number | string; peso_bruto?: number | string; cubagem_m3?: number | string
  tipo: string; finalidade?: string; ativo: boolean
  produto_pai_id?: number | null
  ml_item_id?: string; ml_variation_id?: string; ml_anuncio_tipo?: string
  margem_sugestao_pct?: number | string
}

interface KitComp {
  id: number; kit_produto_id: number; componente_produto_id: number
  quantidade: number | string; percentage_share?: number | string | null; ordem: number
  componente_codigo?: string; componente_descricao?: string
  componente_preco_custo?: number | string; componente_preco_venda?: number | string
  componente_unidade?: string
  componente_estoque?: number | string | null
  componente_estoque_consolidado?: number | string | null
}

// Formata quantidade de estoque sem zeros inúteis (3.000 → 3; 2.500 → 2,5)
const fmtEstoque = (n: any) => Number(n || 0).toFixed(3).replace(/\.?0+$/, '')

interface UnidadeConv {
  id: number; produto_base_id: number
  codigo: string; ean?: string; unidade: string; descricao_alt?: string
  fator: number | string; preco_venda?: number | string
  ml_item_id?: string; ml_variation_id?: string
  ativo: boolean
}

interface ProdutoFoto {
  id: number
  produto_id: number
  url_thumb: string
  url_medium: string
  url_full: string
  nome_original?: string
  mime_type?: string
  tamanho_bytes?: number
  largura?: number
  altura?: number
  principal: boolean
  ordem: number
}

const EMPTY: Produto = {
  codigo: '', descricao: '', unidade: 'UN',
  preco_custo: 0, preco_venda: 0,
  estoque_atual: 0, estoque_minimo: 0,
  origem: '0', tipo: 'produto', finalidade: 'revenda', ativo: true,
  saida_por: 'FIFO',
  conta_gerencial_id: null,
  csosn: '102', cst_pis: '01', cst_cofins: '01',
}

const ORIGEM_OPCOES = [
  { v: '0', t: '0 · Nacional' },
  { v: '1', t: '1 · Estrangeira - Imp. direta' },
  { v: '2', t: '2 · Estrangeira - Adq. mercado interno' },
  { v: '3', t: '3 · Nacional - conteúdo importado > 40%' },
  { v: '4', t: '4 · Nacional - Dec-Lei 288' },
  { v: '5', t: '5 · Nacional - conteúdo importado ≤ 40%' },
  { v: '6', t: '6 · Estrangeira - direta sem similar' },
  { v: '7', t: '7 · Estrangeira - sem similar' },
  { v: '8', t: '8 · Nacional - conteúdo importado > 70%' },
]
const CSOSN_OPCOES = [
  { v: '101', t: '101 · Tributada c/ crédito' },
  { v: '102', t: '102 · Tributada s/ crédito' },
  { v: '103', t: '103 · Isenção ICMS' },
  { v: '201', t: '201 · Tributada c/ crédito + ST' },
  { v: '202', t: '202 · Tributada s/ crédito + ST' },
  { v: '203', t: '203 · Isenção + ST' },
  { v: '300', t: '300 · Imune' },
  { v: '400', t: '400 · Não tributada' },
  { v: '500', t: '500 · Cobrado anteriormente por ST' },
  { v: '900', t: '900 · Outros' },
]
const CST_PIS_COFINS = [
  { v: '01', t: '01 · Tributável - alíq. básica' },
  { v: '02', t: '02 · Tributável - alíq. diferenciada' },
  { v: '04', t: '04 · Monofásica - alíq. zero' },
  { v: '06', t: '06 · Alíquota zero' },
  { v: '07', t: '07 · Isenta' },
  { v: '08', t: '08 · Sem incidência' },
  { v: '09', t: '09 · Com suspensão' },
  { v: '49', t: '49 · Outras operações de saída' },
  { v: '99', t: '99 · Outras operações' },
]
const TIPO_PRODUTO = [
  { v: 'produto', t: 'Produto simples' },
  { v: 'grade', t: 'Produto com grade (pai)' },
  { v: 'variacao', t: 'Variação (filho de grade)' },
  { v: 'kit', t: 'Kit / Combo' },
  { v: 'servico', t: 'Serviço' },
  { v: 'materia_prima', t: 'Matéria-prima' },
]

// Finalidade é ortogonal ao tipo. Define CFOP/CSOSN sugeridos e regras de uso.
const FINALIDADE_PRODUTO: { v: string; t: string; emoji: string; cor: string; cfop_venda?: string; csosn?: string; permite_venda: boolean; hint: string }[] = [
  { v: 'revenda', t: 'Revenda / Mercadoria', emoji: '🛒', cor: 'bg-green-100 text-green-700', cfop_venda: '5102', csosn: '102', permite_venda: true, hint: 'Produto comprado para revender. CFOP 5102 / CSOSN 102.' },
  { v: 'consumo', t: 'Uso e consumo interno', emoji: '🧰', cor: 'bg-amber-100 text-amber-700', cfop_venda: '5949', csosn: '900', permite_venda: false, hint: 'Material de escritório, limpeza, manutenção. Não é vendido.' },
  { v: 'industrializacao', t: 'Industrialização / Insumo', emoji: '🏭', cor: 'bg-blue-100 text-blue-700', cfop_venda: '5101', csosn: '101', permite_venda: true, hint: 'Matéria-prima ou insumo que entra na produção. CFOP 5101 (produção própria).' },
  { v: 'materia_prima', t: 'Matéria-prima', emoji: '🧪', cor: 'bg-teal-100 text-teal-700', cfop_venda: '5101', csosn: '101', permite_venda: false, hint: 'Insumo que entra na produção (aparece na Sugestão de Compra de MP). Não é vendido direto.' },
  { v: 'imobilizado', t: 'Imobilizado / Patrimônio', emoji: '🏢', cor: 'bg-purple-100 text-purple-700', cfop_venda: '5551', csosn: '900', permite_venda: false, hint: 'Bem do ativo permanente (máquina, móvel, equipamento). Não vai pra venda.' },
  { v: 'comodato', t: 'Comodato (freezer)', emoji: '❄️', cor: 'bg-cyan-100 text-cyan-700', cfop_venda: '5908', csosn: '400', permite_venda: false, hint: 'Bem cedido em comodato ao cliente (freezer). Remessa CFOP 5908/6908, ICMS não tributado. Aparece no módulo Comodato.' },
  { v: 'embalagem', t: 'Embalagem', emoji: '📦', cor: 'bg-pink-100 text-pink-700', cfop_venda: '5949', csosn: '102', permite_venda: false, hint: 'Embalagem do produto (caixa, sacola). Geralmente não vendida isolada.' },
  { v: 'servico', t: 'Serviço', emoji: '🔧', cor: 'bg-indigo-100 text-indigo-700', cfop_venda: '5933', csosn: '900', permite_venda: true, hint: 'Serviço / mão de obra (oficina, bikefit). Tributado por ISS / NFS-e, sem NCM.' },
  { v: 'outros', t: 'Outros', emoji: '❓', cor: 'bg-gray-100 text-gray-700', cfop_venda: '5949', csosn: '900', permite_venda: true, hint: 'Categoria genérica. Configure CFOP/CSOSN manualmente.' },
]
const FINALIDADE_BY_V: Record<string, typeof FINALIDADE_PRODUTO[0]> = Object.fromEntries(FINALIDADE_PRODUTO.map(f => [f.v, f]))

export default function ProdutosPage() {
  const qc = useQueryClient()
  const { empresa } = useAuth()
  const showBorrachaTab = !!(empresa?.id && EMPRESAS_COM_GRADES.has(empresa.id))
  const [q, setQ] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Produto>(EMPTY)
  const [tab, setTab] = useState<'geral' | 'fiscal' | 'pesos' | 'kit' | 'unidades' | 'fornecedores' | 'marketplace' | 'galeria' | 'filiais' | 'borracha'>('geral')
  const [etiqOpen, setEtiqOpen] = useState(false)
  // Produto cujo estoque por loja está sendo consultado (modal "onde tem")
  const [verEstoqueDe, setVerEstoqueDe] = useState<Produto | null>(null)
  // Componentes do kit em criação (produto novo ainda sem id) — gravados no Salvar
  const [pendingKitComps, setPendingKitComps] = useState<any[]>([])
  const [filtroGrupo, setFiltroGrupo] = useState<string>('')
  const [filtroSubgrupo, setFiltroSubgrupo] = useState<string>('')
  const [filtroTipo, setFiltroTipo] = useState<string>('')
  const [filtroFinalidade, setFiltroFinalidade] = useState<string>('')
  const [filtroEstoque, setFiltroEstoque] = useState<string>('') // '', 'com', 'sem', 'abaixo_minimo'
  const [filtroAtivo, setFiltroAtivo] = useState<string>('ativo') // 'ativo', 'inativo', 'todos'
  const [filtroFiscal, setFiltroFiscal] = useState<string>('') // '', 'ok', 'pendente'
  const PAGE_SIZE = 50
  const [page, setPage] = useState(0)
  // volta pra 1ª página sempre que algum filtro muda
  useEffect(() => { setPage(0) }, [q, filtroGrupo, filtroSubgrupo, filtroTipo, filtroFinalidade, filtroAtivo, filtroEstoque, filtroFiscal])

  const { data: pageData, isLoading, isFetching } = useQuery<{ rows: Produto[]; total: number }>({
    queryKey: ['produtos', q, filtroGrupo, filtroSubgrupo, filtroTipo, filtroFinalidade, filtroAtivo, filtroEstoque, filtroFiscal, page],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api.get('/cadastros/produtos', { params: {
        q: q || undefined,
        grupo: filtroGrupo || undefined,
        subgrupo: filtroSubgrupo || undefined,
        tipo: filtroTipo || undefined,
        finalidade: filtroFinalidade || undefined,
        ativo: filtroAtivo || undefined,       // 'ativo' | 'inativo' | 'todos'
        estoque: filtroEstoque || undefined,
        fiscal: filtroFiscal || undefined,
        limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      } })
      return { rows: res.data as Produto[], total: Number(res.headers['x-total-count'] ?? res.data.length) }
    },
  })
  const produtos = pageData?.rows ?? []
  const total = pageData?.total ?? 0
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const inicio = total === 0 ? 0 : page * PAGE_SIZE + 1
  const fim = Math.min(page * PAGE_SIZE + produtos.length, total)

  const { data: categorias = [] } = useQuery<{ id: number; nome: string; parent_id: number | null }[]>({
    queryKey: ['cadastros-categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })
  const grupos = useMemo(() => categorias.filter(c => c.parent_id == null), [categorias])
  const subgrupos = useMemo(() =>
    filtroGrupo ? categorias.filter(c => c.parent_id === Number(filtroGrupo)) : [],
    [categorias, filtroGrupo]
  )
  const categoriasMap = useMemo(() => {
    const m: Record<number, { id: number; nome: string; parent_id: number | null }> = {}
    categorias.forEach(c => { m[c.id] = c })
    return m
  }, [categorias])

  const limparFiltros = () => {
    setQ(''); setFiltroGrupo(''); setFiltroSubgrupo(''); setFiltroTipo(''); setFiltroFinalidade('')
    setFiltroEstoque(''); setFiltroAtivo('ativo'); setFiltroFiscal('')
  }
  const filtrosAtivos = (q ? 1 : 0) + (filtroGrupo ? 1 : 0) + (filtroSubgrupo ? 1 : 0)
    + (filtroTipo ? 1 : 0) + (filtroFinalidade ? 1 : 0) + (filtroEstoque ? 1 : 0) + (filtroAtivo !== 'ativo' ? 1 : 0)
    + (filtroFiscal ? 1 : 0)

  const save = useMutation({
    mutationFn: async (p: Produto) => {
      if (p.id) return (await api.put(`/cadastros/produtos/${p.id}`, p)).data
      // Produto novo: se for kit, manda os componentes escolhidos inline junto
      const body: any = { ...p }
      if (p.tipo === 'kit' && pendingKitComps.length) {
        body.componentes = pendingKitComps.map(c => ({
          componente_produto_id: c.componente_produto_id,
          quantidade: c.quantidade,
          percentage_share: c.percentage_share,
        }))
      }
      return (await api.post('/cadastros/produtos', body)).data
    },
    onSuccess: (saved: Produto) => {
      toast.success('Produto salvo')
      qc.invalidateQueries({ queryKey: ['produtos'] })
      // Kit: mantém o form aberto já na aba de componentes (agora com id salvo,
      // a TabKit completa assume — sugestão de preço, ajustes). Limpa o pendente.
      if (saved.tipo === 'kit') {
        setPendingKitComps([])
        setEditing(saved)
        setTab('kit')
      } else if (!editing.id && saved.id) {
        // Produto novo: mantém aberto pra usuário adicionar unidades/etc
        setEditing(saved)
      } else {
        setShowForm(false); setEditing(EMPTY); setTab('geral')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  // Excluir produto: o backend apaga de fato se nunca foi usado, ou INATIVA se
  // já tem vendas/estoque (some da lista padrão). Mensagem vem do backend.
  const excluir = useMutation({
    mutationFn: async (p: Produto) => (await api.delete(`/cadastros/produtos/${p.id}`)).data,
    onSuccess: (r: any) => {
      toast.success(r?.mensagem || 'Produto excluído')
      qc.invalidateQueries({ queryKey: ['produtos'] })
    },
    onError: (e: any) => { toast.error(e?.response?.data?.detail || 'Erro ao excluir') },
  })

  const abrirNovoProduto = async () => {
    setEditing(EMPTY); setTab('geral'); setShowForm(true); setPendingKitComps([])
    try {
      const { data } = await api.get('/cadastros/produtos/proximo-codigo')
      if (data?.codigo) setEditing(prev => ({ ...prev, codigo: data.codigo }))
    } catch {}
  }

  // Duplicar produto: abre o cadastro já preenchido com os dados do produto-base,
  // mas como PRODUTO NOVO (sem id) → ao salvar, o backend cria um registro novo.
  // Campos únicos/que não podem repetir são limpos: código (puxa próximo sequencial),
  // EAN (código de barras é único) e vínculos do Mercado Livre. Estoque zera (produto
  // novo nasce sem saldo). O usuário ajusta o que quiser e salva.
  const duplicarProduto = async (p: Produto) => {
    const { id, ...resto } = p
    const copia: Produto = {
      ...resto,
      codigo: '',
      ean: '',
      descricao: `${p.descricao || ''} (cópia)`.trim(),
      estoque_atual: 0,
      ml_item_id: undefined,
      ml_variation_id: undefined,
      ml_anuncio_tipo: undefined,
    }
    setEditing(copia); setTab('geral'); setShowForm(true); setPendingKitComps([])
    try {
      const { data } = await api.get('/cadastros/produtos/proximo-codigo')
      if (data?.codigo) setEditing(prev => ({ ...prev, codigo: data.codigo }))
    } catch {}
    toast.success('Produto copiado — ajuste os detalhes e salve como novo')
  }

  const bulkDefaults = useMutation({
    mutationFn: async () => (await api.post('/cadastros/produtos/bulk-fiscal-defaults', {})).data,
    onSuccess: (d: { total_atualizados: number; mensagem: string }) => {
      qc.invalidateQueries({ queryKey: ['produtos'] })
      toast.success(d.mensagem || `${d.total_atualizados} produto(s) atualizado(s)`)
    },
    onError: () => toast.error('Erro ao aplicar defaults fiscais'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-9" placeholder="Buscar por código, descrição ou EAN..."
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={enterAbrePrimeiro(produtos, p => { setEditing(p); setTab('geral'); setShowForm(true) })} />
        </div>
        <button
          className="btn-ghost text-sm"
          onClick={() => {
            if (confirm('Aplicar defaults fiscais (CFOP=5102, CSOSN=102, CST PIS/COFINS=49, origem=0) nos produtos que estao vazios?\n\nNCM nao eh preenchido automaticamente — voce precisa completar manualmente.'))
              bulkDefaults.mutate()
          }}
          disabled={bulkDefaults.isPending}
          title="Preenche campos fiscais vazios com defaults seguros"
        >
          {bulkDefaults.isPending
            ? <><Loader2 size={14} className="animate-spin" /> Aplicando...</>
            : <><Sparkles size={14} /> Defaults fiscais</>}
        </button>
        <button className="btn-ghost text-sm" onClick={() => setEtiqOpen(true)} title="Imprimir etiquetas de produtos (Pimaco 6181 A4)">
          <Tag size={14} /> Etiquetas
        </button>
        <button className="btn-primary" onClick={abrirNovoProduto}>
          <Plus size={16} /> Novo Produto
        </button>
      </div>

      <UltimosCadastros
        endpoint="/cadastros/produtos/recentes"
        queryKey={['produtos-recentes']}
        rotulo="produto"
        onSelecionar={async (id) => {
          try {
            const p = (await api.get(`/cadastros/produtos/${id}`)).data
            setEditing(p); setTab('geral'); setShowForm(true)
          } catch { toast.error('Não foi possível abrir o produto') }
        }}
      />

      {/* Filtros */}
      <div className="card card-body bg-ink-50/50 border border-ink-200">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-600 font-medium uppercase tracking-wide">Filtros:</span>
          <select className="input !py-1.5 !text-sm w-auto min-w-[140px]"
            value={filtroGrupo}
            onChange={e => { setFiltroGrupo(e.target.value); setFiltroSubgrupo('') }}>
            <option value="">Todos os grupos</option>
            {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
          <select className="input !py-1.5 !text-sm w-auto min-w-[140px]"
            value={filtroSubgrupo}
            onChange={e => setFiltroSubgrupo(e.target.value)}
            disabled={!filtroGrupo || subgrupos.length === 0}>
            <option value="">{filtroGrupo ? 'Todos os subgrupos' : '(escolha um grupo)'}</option>
            {subgrupos.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
          <select className="input !py-1.5 !text-sm w-auto"
            value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Todos os tipos</option>
            <option value="produto">Simples</option>
            <option value="grade">Grade</option>
            <option value="variacao">Variação</option>
            <option value="kit">Kit</option>
            <option value="servico">Serviço</option>
            <option value="materia_prima">Insumo</option>
          </select>
          <select className="input !py-1.5 !text-sm w-auto"
            value={filtroFinalidade} onChange={e => setFiltroFinalidade(e.target.value)}>
            <option value="">Todas finalidades</option>
            {FINALIDADE_PRODUTO.map(f => <option key={f.v} value={f.v}>{f.emoji} {f.t}</option>)}
          </select>
          <select className="input !py-1.5 !text-sm w-auto"
            value={filtroEstoque} onChange={e => setFiltroEstoque(e.target.value)}>
            <option value="">Estoque (todos)</option>
            <option value="com">Com estoque</option>
            <option value="sem">Sem estoque</option>
            <option value="abaixo_minimo">Abaixo do mínimo</option>
          </select>
          <select className="input !py-1.5 !text-sm w-auto"
            value={filtroAtivo} onChange={e => setFiltroAtivo(e.target.value)}>
            <option value="ativo">Apenas ativos</option>
            <option value="inativo">Apenas inativos</option>
            <option value="todos">Todos</option>
          </select>
          <select className="input !py-1.5 !text-sm w-auto"
            value={filtroFiscal} onChange={e => setFiltroFiscal(e.target.value)}>
            <option value="">Fiscal (todos)</option>
            <option value="ok">Fiscal OK</option>
            <option value="pendente">Fiscal pendente</option>
          </select>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="btn-ghost text-xs">
              <X size={12} /> Limpar ({filtrosAtivos})
            </button>
          )}
          <span className="ml-auto text-xs text-ink-500">
            {total.toLocaleString('pt-BR')} produto{total !== 1 ? 's' : ''}
            {isFetching && <Loader2 size={12} className="inline ml-1.5 animate-spin text-ink-400" />}
          </span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Tipo</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Finalidade</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Descrição</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Grupo</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">EAN</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">NCM</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Preço</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Estoque</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Fiscal</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={11} className="text-center py-6 text-ink-400">Carregando...</td></tr>}
            {!isLoading && produtos.length === 0 && (
              <tr><td colSpan={11} className="text-center py-6 text-ink-400">
                {filtrosAtivos > 0 ? 'Nenhum produto corresponde aos filtros' : 'Nenhum produto cadastrado'}
              </td></tr>
            )}
            {produtos.map(p => {
              const fiscalOk = !!(p.ncm && p.cfop && (p.csosn || p.cst) && p.cst_pis && p.cst_cofins)
              const tipoBadge = {
                produto: { cor: 'bg-slate-100 text-slate-700', txt: 'Simples' },
                grade: { cor: 'bg-purple-100 text-purple-700', txt: 'Grade' },
                variacao: { cor: 'bg-indigo-100 text-indigo-700', txt: 'Variação' },
                kit: { cor: 'bg-amber-100 text-amber-700', txt: '🎁 Kit' },
                servico: { cor: 'bg-blue-100 text-blue-700', txt: 'Serviço' },
                materia_prima: { cor: 'bg-teal-100 text-teal-700', txt: 'Insumo' },
              }[p.tipo] || { cor: 'bg-gray-100 text-gray-700', txt: p.tipo }
              const finBadge = FINALIDADE_BY_V[p.finalidade || 'revenda'] || FINALIDADE_BY_V.outros
              return (
                <tr key={p.id}
                  onClick={() => { setEditing(p); setTab('geral'); setShowForm(true) }}
                  className="hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tipoBadge.cor}`}>
                      {tipoBadge.txt}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${finBadge.cor}`} title={finBadge.hint}>
                      {finBadge.emoji} {finBadge.t.split(' / ')[0]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{p.codigo}</td>
                  <td className="px-3 py-2.5 text-sm font-medium">{p.descricao}</td>
                  <td className="px-3 py-2.5 text-xs text-ink-600">
                    {(() => {
                      const cat = p.categoria_id ? categoriasMap[p.categoria_id] : null
                      if (!cat) return <span className="text-ink-300">—</span>
                      const grupo = cat.parent_id ? categoriasMap[cat.parent_id] : cat
                      const sub = cat.parent_id ? cat : null
                      return (
                        <div className="leading-tight">
                          <div className="font-medium text-ink-700">{grupo?.nome ?? '—'}</div>
                          {sub && <div className="text-[10px] text-ink-500">› {sub.nome}</div>}
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-ink-500">{p.ean || '—'}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-ink-500">{p.ncm || '—'}</td>
                  <td className="px-3 py-2.5 text-sm text-right">{formatMoney(p.preco_venda)}</td>
                  <td className="px-3 py-2.5 text-sm text-right" onClick={e => e.stopPropagation()}>
                    {p.tipo === 'kit' ? <span className="text-amber-600">kit</span>
                      : p.tipo === 'grade' ? <span className="text-purple-600">(pai)</span>
                      : (() => {
                        const local = Number(p.estoque_atual || 0)
                        const total = Number(p.estoque_consolidado ?? local)
                        const temFora = total > local
                        return (
                          <button
                            onClick={() => setVerEstoqueDe(p)}
                            title="Ver estoque em cada loja"
                            className="inline-flex flex-col items-end hover:text-brand-700 group">
                            <span>
                              {local.toFixed(3).replace(/\.?0+$/, '')} {p.unidade}
                            </span>
                            {temFora && (
                              <span className="text-[10px] text-sky-700 flex items-center gap-0.5 group-hover:underline">
                                <Store size={9} />
                                {total.toFixed(3).replace(/\.?0+$/, '')} no total
                              </span>
                            )}
                          </button>
                        )
                      })()}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {fiscalOk
                      ? <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">OK</span>
                      : <span className="text-[10px] font-semibold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">⚠ NFe</span>}
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setEditing(p); setTab('geral'); setShowForm(true) }}
                        className="text-ink-500 hover:text-brand-600" title="Editar">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => duplicarProduto(p)}
                        className="text-ink-500 hover:text-brand-600" title="Copiar para um novo produto">
                        <Copy size={16} />
                      </button>
                      <button onClick={() => pedirExclusao({ descricao: `o produto "${p.descricao}"`, onConfirm: () => excluir.mutate(p) })}
                        className="text-ink-500 hover:text-red-600" title="Excluir produto">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINAÇÃO */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-ink-600">
          <span>Mostrando <b>{inicio}</b>–<b>{fim}</b> de <b>{total.toLocaleString('pt-BR')}</b></span>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="px-2">Página {page + 1} de {totalPaginas}</span>
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPage(p => Math.min(totalPaginas - 1, p + 1))} disabled={page >= totalPaginas - 1}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-white flex-shrink-0">
              <div>
                <h3 className="font-semibold">
                  {editing.id ? `Editar: ${editing.codigo}` : 'Novo Produto'}
                </h3>
                {editing.id && <p className="text-xs text-ink-500">ID #{editing.id} · tipo={editing.tipo}</p>}
              </div>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div className="border-b flex px-5 bg-ink-50/50 overflow-x-auto flex-shrink-0" style={{ minHeight: 44 }}>
              <TabBtn active={tab === 'geral'} onClick={() => setTab('geral')}>
                Geral
              </TabBtn>
              <TabBtn active={tab === 'fiscal'} onClick={() => setTab('fiscal')}>
                <Receipt size={14} /> Fiscal / NF-e
              </TabBtn>
              <TabBtn active={tab === 'pesos'} onClick={() => setTab('pesos')}>
                <Scale size={14} /> Pesos
              </TabBtn>
              {editing.tipo === 'kit' && (
                <TabBtn active={tab === 'kit'} onClick={() => setTab('kit')}>
                  <Package size={14} /> Componentes do Kit
                </TabBtn>
              )}
              {editing.id && editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
                <TabBtn active={tab === 'unidades'} onClick={() => setTab('unidades')}>
                  <Boxes size={14} /> Unidades alternativas
                </TabBtn>
              )}
              {editing.id && (
                <TabBtn active={tab === 'marketplace'} onClick={() => setTab('marketplace')}>
                  🛒 Marketplace
                </TabBtn>
              )}
              {editing.id && (
                <TabBtn active={tab === 'galeria'} onClick={() => setTab('galeria')}>
                  <ImageIcon size={14} /> Galeria
                </TabBtn>
              )}
              {editing.id && (
                <TabBtn active={tab === 'fornecedores'} onClick={() => setTab('fornecedores')}>
                  🚚 Fornecedores
                </TabBtn>
              )}
              {editing.id && (
                <TabBtn active={tab === 'filiais'} onClick={() => setTab('filiais')}>
                  🏢 Filiais
                </TabBtn>
              )}
              {showBorrachaTab && editing.id && (
                <TabBtn active={tab === 'borracha'} onClick={() => setTab('borracha')}>
                  🎨 Borracha
                </TabBtn>
              )}
            </div>

            <div className="flex-1 overflow-auto min-h-0">
              {tab === 'geral' && <TabGeral editing={editing} setEditing={setEditing} />}
              {tab === 'fiscal' && <TabFiscal editing={editing} setEditing={setEditing} />}
              {tab === 'pesos' && <TabPesos editing={editing} setEditing={setEditing} />}
              {tab === 'kit' && (editing.id
                ? <TabKit produtoId={editing.id} />
                : <TabKitLocal comps={pendingKitComps} setComps={setPendingKitComps}
                    onSalvar={() => save.mutate(editing)} saving={save.isPending}
                    canSave={!!editing.codigo && !!editing.descricao} />
              )}
              {tab === 'unidades' && editing.id && <TabUnidades produtoId={editing.id} />}
              {tab === 'fornecedores' && editing.id && <TabFornecedores produtoId={editing.id} />}
              {tab === 'marketplace' && editing.id && <TabMarketplace editing={editing} setEditing={setEditing} />}
              {tab === 'galeria' && editing.id && <TabGaleria produtoId={editing.id} />}
              {tab === 'filiais' && editing.id && <div className="p-5"><FilialLiberacoes entidade="produto" entidadeId={editing.id} /></div>}
              {tab === 'borracha' && editing.id && <TabBorracha produtoId={editing.id} />}
            </div>

            <div className="px-5 py-4 border-t flex gap-2 bg-white flex-shrink-0">
              <button onClick={() => { setShowForm(false); setEditing(EMPTY); setTab('geral') }}
                className="btn-secondary flex-1">Fechar</button>
              <button onClick={() => save.mutate(editing)}
                disabled={!editing.codigo || !editing.descricao || save.isPending ||
                  (!editing.id && editing.tipo !== 'servico' && !editing.categoria_id)}
                className="btn-primary flex-1">
                {save.isPending
                  ? <><Loader2 size={14} className="animate-spin inline" /> Salvando...</>
                  : (editing.id ? 'Salvar alterações' : 'Criar e continuar')}
              </button>
            </div>
          </div>
        </div>
      )}
      {etiqOpen && <EtiquetasModal onClose={() => setEtiqOpen(false)} />}
      {verEstoqueDe && (
        <EstoquePorLojaModal produto={verEstoqueDe} onClose={() => setVerEstoqueDe(null)} />
      )}
    </div>
  )
}

// ============ TAB BUTTON ============
function SalvarPrimeiro() {
  return (
    <div className="p-8 text-center text-ink-500 text-sm">
      💾 Salve o produto na aba <strong>Geral</strong> antes pra usar essa aba.
    </div>
  )
}

function TabBtn({ active, onClick, children }: any) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-1 whitespace-nowrap ${
        active ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500'
      }`}>
      {children}
    </button>
  )
}

// ============ TAB GERAL ============
function TabGeral({ editing, setEditing }: any) {
  const finalidade = FINALIDADE_BY_V[editing.finalidade || 'revenda'] || FINALIDADE_BY_V.outros
  const handleFinalidade = (v: string) => {
    const f = FINALIDADE_BY_V[v]
    setEditing((p: any) => ({
      ...p,
      finalidade: v,
      // Sugere CFOP/CSOSN se ainda não foi customizado pelo user
      cfop: p.cfop && p.cfop !== FINALIDADE_BY_V[p.finalidade || 'revenda']?.cfop_venda ? p.cfop : f.cfop_venda,
      csosn: p.csosn && p.csosn !== FINALIDADE_BY_V[p.finalidade || 'revenda']?.csosn ? p.csosn : f.csosn,
    }))
  }
  return (
    <div className="p-5 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">Tipo do produto</label>
          <select className="input" value={editing.tipo}
            onChange={e => setEditing((p: any) => ({ ...p, tipo: e.target.value }))}>
            {TIPO_PRODUTO.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Finalidade</label>
          <select className="input" value={editing.finalidade || 'revenda'} onChange={e => handleFinalidade(e.target.value)}>
            {FINALIDADE_PRODUTO.map(o => <option key={o.v} value={o.v}>{o.emoji} {o.t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Unidade comercial</label>
          <input className="input" value={editing.unidade}
            onChange={e => setEditing((p: any) => ({ ...p, unidade: e.target.value.toUpperCase() }))} />
        </div>
      </div>
      <div className="text-xs text-ink-600 bg-ink-50 px-3 py-2 rounded border-l-4 border-brand-400">
        <strong>{finalidade.emoji} {finalidade.t}:</strong> {finalidade.hint}
        {!finalidade.permite_venda && (
          <span className="ml-2 inline-block bg-red-100 text-red-700 px-2 py-0.5 rounded text-[11px] font-semibold">
            ⚠️ não permite venda direta
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Código / SKU</label>
          <input className="input" value={editing.codigo}
            onChange={e => setEditing((p: any) => ({ ...p, codigo: e.target.value }))} />
        </div>
        <div>
          <label className="label">EAN / GTIN</label>
          <input className="input" value={editing.ean || ''}
            onChange={e => setEditing((p: any) => ({ ...p, ean: e.target.value }))} />
        </div>
      </div>
      <div className="grid grid-cols-[1fr,200px] gap-3">
        <div>
          <label className="label">Descrição</label>
          <input className="input" value={editing.descricao}
            onChange={e => setEditing((p: any) => ({ ...p, descricao: e.target.value }))} />
        </div>
        <div>
          <label className="label">Marca</label>
          <input className="input" value={editing.marca || ''}
            onChange={e => setEditing((p: any) => ({ ...p, marca: e.target.value }))}
            placeholder="Ex: Shimano" title="Marca/fabricante (relatório de vendas por marca)" />
        </div>
      </div>
      <GrupoSubgrupoSelect editing={editing} setEditing={setEditing} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Preço custo</label>
          <input className="input" type="number" step="0.01" value={editing.preco_custo}
            onChange={e => setEditing((p: any) => ({ ...p, preco_custo: parseFloat(e.target.value) || 0 }))} />
        </div>
        <div>
          <label className="label">Preço venda</label>
          <input className="input" type="number" step="0.01" value={editing.preco_venda}
            onChange={e => setEditing((p: any) => ({ ...p, preco_venda: parseFloat(e.target.value) || 0 }))} />
        </div>
        <div>
          <label className="label">Comissão %</label>
          <input className="input" type="number" step="0.01" value={(editing as any).comissao_percent ?? 0}
            onChange={e => setEditing((p: any) => ({ ...p, comissao_percent: parseFloat(e.target.value) || 0 }))}
            title="% de comissão do produto (usado no relatório de comissão)" />
        </div>
      </div>
      {editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Estoque atual</label>
            <input className="input" type="number" step="0.001" value={editing.estoque_atual}
              onChange={e => setEditing((p: any) => ({ ...p, estoque_atual: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="label">Estoque mínimo</label>
            <input className="input" type="number" step="0.001" value={editing.estoque_minimo}
              onChange={e => setEditing((p: any) => ({ ...p, estoque_minimo: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label className="label">Endereço estoque</label>
            <input className="input" value={editing.endereco_estoque || ''}
              onChange={e => setEditing((p: any) => ({ ...p, endereco_estoque: e.target.value }))}
              placeholder="A-01-02" />
          </div>
        </div>
      )}
      {editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
        <div>
          <label className="label">Método de saída de estoque</label>
          <select className="input" value={editing.saida_por || 'FIFO'}
            onChange={e => setEditing((p: any) => ({ ...p, saida_por: e.target.value }))}>
            <option value="FIFO">FIFO - primeiro a entrar sai primeiro</option>
            <option value="FEFO">FEFO - primeiro a vencer sai primeiro</option>
            <option value="MANUAL">MANUAL - escolher lote ao separar</option>
          </select>
        </div>
      )}
      {editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
        <div className="border-l-4 border-brand-400 bg-brand-50 p-3 rounded">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-1"
              checked={!!editing.is_industrial}
              onChange={e => setEditing((p: any) => ({ ...p, is_industrial: e.target.checked }))} />
            <div>
              <div className="text-sm font-medium">Produto industrial (fabricado internamente)</div>
              <div className="text-xs text-ink-600">
                Ao confirmar um pedido de venda com este produto, o sistema gera automaticamente uma <strong>Ordem de Produção proposta</strong> (caso o estoque não cubra a quantidade). O PCP aprova antes de virar OP planejada.
                Requer ficha técnica ativa vinculada.
              </div>
            </div>
          </label>
        </div>
      )}
      {editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
        <div>
          <label className="label">Prazo de validade (dias após produção)</label>
          <input className="input" type="number" min="0"
            value={editing.shelf_life_dias ?? 0}
            onChange={e => setEditing((p: any) => ({ ...p, shelf_life_dias: parseInt(e.target.value) || 0 }))}
            placeholder="0 = sem controle de validade" />
          <p className="text-xs text-ink-500 mt-1">
            Quando &gt; 0, cada lote de produção recebe automaticamente a data de validade: data da produção + X dias.
          </p>
        </div>
      )}
      {editing.is_industrial && editing.tipo !== 'kit' && editing.tipo !== 'grade' && (
        <ReciclagemCampo editing={editing} setEditing={setEditing} />
      )}
      {editing.tipo === 'kit' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          🎁 <strong>Kit</strong>: o estoque não é controlado diretamente. Ele é consumido dos componentes.
          Após salvar, vá na tab "Componentes do Kit" para adicionar os itens que compõem este kit.
        </div>
      )}
      {editing.tipo === 'grade' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-900">
          🎨 <strong>Produto com grade</strong>: este é o produto pai. Crie variações filhas
          (tipo "Variação") referenciando este produto pai.
        </div>
      )}
    </div>
  )
}

// ============ GRUPO / SUBGRUPO ============
interface CategoriaItem { id: number; nome: string; parent_id: number | null; parent_nome?: string | null; ativo: boolean }

function GrupoSubgrupoSelect({ editing, setEditing }: any) {
  const qc = useQueryClient()
  const [showNew, setShowNew] = useState<null | { tipo: 'grupo' | 'subgrupo' }>(null)
  const [novoNome, setNovoNome] = useState('')

  const { data: cats = [] } = useQuery<CategoriaItem[]>({
    queryKey: ['cadastros-categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })

  const grupos = useMemo(() => cats.filter(c => c.parent_id == null && c.ativo), [cats])

  // Resolve grupo selecionado a partir da categoria escolhida no produto.
  // editing.categoria_id pode ser um grupo (sem subgrupo) ou um subgrupo.
  const catSelecionada = cats.find(c => c.id === editing.categoria_id)
  const grupoSelecionadoId = catSelecionada
    ? (catSelecionada.parent_id ?? catSelecionada.id)
    : (editing._grupo_id ?? null)

  const subgrupos = useMemo(
    () => grupoSelecionadoId ? cats.filter(c => c.parent_id === grupoSelecionadoId && c.ativo) : [],
    [cats, grupoSelecionadoId]
  )

  const subgrupoSelecionadoId = catSelecionada?.parent_id ? catSelecionada.id : null

  const setGrupo = (gid: number | null) => {
    // Ao trocar grupo: produto passa a referenciar o grupo (sem subgrupo)
    setEditing((p: any) => ({ ...p, categoria_id: gid ?? undefined, _grupo_id: gid }))
  }
  const setSubgrupo = (sid: number | null) => {
    // Subgrupo é o categoria_id final; se null, mantém só o grupo
    setEditing((p: any) => ({
      ...p,
      categoria_id: sid ?? grupoSelecionadoId ?? undefined,
      _grupo_id: grupoSelecionadoId ?? undefined,
    }))
  }

  const criarMut = useMutation({
    mutationFn: async ({ nome, parent_id }: { nome: string; parent_id: number | null }) =>
      (await api.post('/cadastros/categorias', { nome, parent_id, ativo: true })).data,
    onSuccess: (nova: CategoriaItem) => {
      qc.invalidateQueries({ queryKey: ['cadastros-categorias'] })
      if (nova.parent_id) {
        // criou subgrupo → seleciona
        setEditing((p: any) => ({ ...p, categoria_id: nova.id, _grupo_id: nova.parent_id }))
      } else {
        setGrupo(nova.id)
      }
      setShowNew(null); setNovoNome('')
      toast.success(nova.parent_id ? 'Subgrupo criado' : 'Grupo criado')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar'),
  })

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="label flex items-center justify-between">
          <span>
            Grupo
            {!editing.id && editing.tipo !== 'servico' && (
              <span className="text-red-500 ml-0.5">*</span>
            )}
          </span>
          <button type="button" className="text-xs text-brand-700 hover:underline"
            onClick={() => { setShowNew({ tipo: 'grupo' }); setNovoNome('') }}>+ novo</button>
        </label>
        <select className="input"
          value={grupoSelecionadoId ?? ''}
          onChange={e => setGrupo(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— sem grupo —</option>
          {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
        </select>
        {!editing.id && editing.tipo !== 'servico' && !grupoSelecionadoId && (
          <p className="text-xs text-red-500 mt-1">Obrigatório escolher um grupo</p>
        )}
      </div>
      <div>
        <label className="label flex items-center justify-between">
          <span>Subgrupo</span>
          <button type="button"
            className="text-xs text-brand-700 hover:underline disabled:text-ink-300 disabled:no-underline"
            disabled={!grupoSelecionadoId}
            onClick={() => { setShowNew({ tipo: 'subgrupo' }); setNovoNome('') }}>+ novo</button>
        </label>
        <select className="input"
          value={subgrupoSelecionadoId ?? ''}
          disabled={!grupoSelecionadoId}
          onChange={e => setSubgrupo(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— sem subgrupo —</option>
          {subgrupos.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
      </div>

      {showNew && (
        <div className="col-span-2 bg-brand-50 border border-brand-200 rounded p-3 flex items-center gap-2">
          <span className="text-xs font-medium text-brand-900 whitespace-nowrap">
            Novo {showNew.tipo === 'grupo' ? 'grupo' : `subgrupo de "${grupos.find(g => g.id === grupoSelecionadoId)?.nome}"`}:
          </span>
          <input className="input flex-1 text-sm" autoFocus
            value={novoNome} onChange={e => setNovoNome(e.target.value)}
            placeholder="Nome..."
            onKeyDown={e => { if (e.key === 'Enter' && novoNome.trim()) {
              criarMut.mutate({
                nome: novoNome.trim(),
                parent_id: showNew.tipo === 'subgrupo' ? grupoSelecionadoId : null,
              })
            }}} />
          <button type="button" className="btn-primary text-xs" disabled={!novoNome.trim() || criarMut.isPending}
            onClick={() => criarMut.mutate({
              nome: novoNome.trim(),
              parent_id: showNew.tipo === 'subgrupo' ? grupoSelecionadoId : null,
            })}>
            {criarMut.isPending ? '...' : 'Criar'}
          </button>
          <button type="button" className="btn-secondary text-xs" onClick={() => setShowNew(null)}>Cancelar</button>
        </div>
      )}
    </div>
  )
}

// ============ REFUGO VALORIZÁVEL ============
function ReciclagemCampo({ editing, setEditing }: any) {
  const { data: mps = [] } = useQuery<any[]>({
    queryKey: ['mps-reciclagem'],
    queryFn: async () => (await api.get('/industrial/materias-primas')).data,
  })
  return (
    <div className="border-l-4 border-emerald-400 bg-emerald-50 p-3 rounded space-y-2">
      <div className="text-sm font-medium">♻️ Refugo valorizável</div>
      <div className="text-xs text-ink-600">
        Quando uma OP deste produto concluir com refugo, o sistema dá entrada automática em uma matéria-prima reciclada,
        aplicando o fator de aproveitamento abaixo. A MP reciclada pode voltar pra composição de outras fichas.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium">Matéria-prima reciclada (destino)</label>
          <select className="input w-full text-sm"
            value={editing.mp_reciclada_id ?? ''}
            onChange={e => setEditing((p: any) => ({
              ...p, mp_reciclada_id: e.target.value ? Number(e.target.value) : null
            }))}>
            <option value="">— refugo é perda total —</option>
            {mps.map((mp: any) => (
              <option key={mp.id} value={mp.id}>{mp.codigo} — {mp.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">Fator de aproveitamento (%)</label>
          <input type="number" min="0" max="100" step="0.01" className="input w-full text-sm"
            value={editing.refugo_fator_aproveitamento ?? 100}
            onChange={e => setEditing((p: any) => ({
              ...p, refugo_fator_aproveitamento: parseFloat(e.target.value) || 0
            }))} />
          <p className="text-[10px] text-ink-500">Ex: 80% = cada 10 un refugadas geram 8 un na MP.</p>
        </div>
      </div>
    </div>
  )
}

// ============ TAB FISCAL ============
function TabFiscal({ editing, setEditing }: any) {
  const set = (k: string, v: any) => setEditing((p: any) => ({ ...p, [k]: v }))
  return (
    <div className="p-5 space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        💡 Campos obrigatórios pra NF-e. Simples Nacional usa CSOSN. Regime Normal usa CST.
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">NCM <span className="text-red-500">*</span></label>
          <input className="input font-mono" maxLength={8} value={editing.ncm || ''}
            onChange={e => set('ncm', e.target.value.replace(/\D/g, ''))} />
        </div>
        <div>
          <label className="label">CEST (se ST)</label>
          <input className="input font-mono" maxLength={7} value={editing.cest || ''}
            onChange={e => set('cest', e.target.value.replace(/\D/g, ''))} />
        </div>
        <div>
          <label className="label">CFOP padrão</label>
          <input className="input font-mono" maxLength={4} value={editing.cfop || ''}
            onChange={e => set('cfop', e.target.value.replace(/\D/g, ''))} />
        </div>
      </div>
      <div>
        <label className="label">Origem da mercadoria</label>
        <select className="input" value={editing.origem} onChange={e => set('origem', e.target.value)}>
          {ORIGEM_OPCOES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">CSOSN (Simples Nacional)</label>
          <select className="input" value={editing.csosn || ''} onChange={e => set('csosn', e.target.value)}>
            <option value="">—</option>
            {CSOSN_OPCOES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">CST (Regime Normal)</label>
          <input className="input font-mono" maxLength={3} value={editing.cst || ''}
            onChange={e => set('cst', e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2 border-t">
        <div>
          <label className="label">CST PIS</label>
          <select className="input" value={editing.cst_pis || ''} onChange={e => set('cst_pis', e.target.value)}>
            <option value="">—</option>
            {CST_PIS_COFINS.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Alíq. PIS %</label>
          <input className="input" type="number" step="0.01" value={editing.aliquota_pis ?? ''}
            onChange={e => set('aliquota_pis', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
        <div>
          <label className="label">Alíq. ICMS %</label>
          <input className="input" type="number" step="0.01" value={editing.aliquota_icms ?? ''}
            onChange={e => set('aliquota_icms', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">CST COFINS</label>
          <select className="input" value={editing.cst_cofins || ''} onChange={e => set('cst_cofins', e.target.value)}>
            <option value="">—</option>
            {CST_PIS_COFINS.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Alíq. COFINS %</label>
          <input className="input" type="number" step="0.01" value={editing.aliquota_cofins ?? ''}
            onChange={e => set('aliquota_cofins', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
        <div />
      </div>
      {/* IPI — destacado SÓ por filial com perfil "Indústria" (aba Fiscal & NFe da filial).
          Preencher aqui não muda nada nas notas da loja: o produto continua saindo sem o
          grupo <IPI>. A lista traz só os CSTs de SAÍDA — os de entrada (00–05, 49) não
          valem numa nota de venda e só criariam chance de erro. */}
      <div className="pt-2 border-t">
        <h5 className="text-xs font-bold text-ink-500 uppercase mb-2">
          IPI <span className="font-normal normal-case text-ink-400">· só destacado quando o CNPJ emitente é indústria</span>
        </h5>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">CST IPI</label>
            <select className="input" value={editing.cst_ipi || ''} onChange={e => set('cst_ipi', e.target.value)}>
              <option value="">— sem IPI —</option>
              <option value="50">50 · Saída tributada</option>
              <option value="51">51 · Saída tributável c/ alíquota zero</option>
              <option value="52">52 · Saída isenta</option>
              <option value="53">53 · Saída não tributada</option>
              <option value="54">54 · Saída imune</option>
              <option value="99">99 · Outras saídas</option>
            </select>
          </div>
          <div>
            <label className="label">Alíq. IPI %</label>
            <input className="input" type="number" step="0.01" value={editing.aliquota_ipi ?? ''}
              onChange={e => set('aliquota_ipi', e.target.value ? parseFloat(e.target.value) : undefined)} />
          </div>
          <div>
            <label className="label">Cód. enquadramento</label>
            <input className="input font-mono" maxLength={3} placeholder="999"
              value={editing.codigo_enquadramento_ipi || ''}
              onChange={e => set('codigo_enquadramento_ipi', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ TAB PESOS ============
function TabPesos({ editing, setEditing }: any) {
  const set = (k: string, v: any) => setEditing((p: any) => ({ ...p, [k]: v }))
  return (
    <div className="p-5 space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        📦 Pesos obrigatórios pra cálculo de frete.
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">Peso líquido (kg)</label>
          <input className="input" type="number" step="0.001" value={editing.peso_liquido ?? ''}
            onChange={e => set('peso_liquido', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
        <div>
          <label className="label">Peso bruto (kg)</label>
          <input className="input" type="number" step="0.001" value={editing.peso_bruto ?? ''}
            onChange={e => set('peso_bruto', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
        <div>
          <label className="label">Cubagem (m³)</label>
          <input className="input" type="number" step="0.0001" value={editing.cubagem_m3 ?? ''}
            onChange={e => set('cubagem_m3', e.target.value ? parseFloat(e.target.value) : undefined)} />
        </div>
      </div>
      <div className="pt-3 border-t">
        <h5 className="text-xs font-bold text-ink-500 uppercase mb-2">Unidade tributável</h5>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Unidade trib.</label>
            <input className="input" value={editing.unidade_tributavel || ''}
              onChange={e => set('unidade_tributavel', e.target.value.toUpperCase())}
              placeholder={editing.unidade} />
          </div>
          <div>
            <label className="label">Qtd. tributável</label>
            <input className="input" type="number" step="0.0001" value={editing.quantidade_tributavel ?? ''}
              onChange={e => set('quantidade_tributavel', e.target.value ? parseFloat(e.target.value) : undefined)} />
          </div>
          <div>
            <label className="label">GTIN tributável</label>
            <input className="input" value={editing.gtin_tributavel || ''}
              onChange={e => set('gtin_tributavel', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ TAB KIT (componentes) ============
function TabKit({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()
  const [buscaProduto, setBuscaProduto] = useState('')
  const [novoComp, setNovoComp] = useState<{ produto_id?: number; qtd: number; pct: number }>({ qtd: 1, pct: 0 })
  const [margemSugestao, setMargemSugestao] = useState(30)

  const { data: componentes = [], isLoading } = useQuery<KitComp[]>({
    queryKey: ['kit-comps', produtoId],
    queryFn: async () => (await api.get(`/cadastros/produtos/${produtoId}/kit-componentes`)).data,
  })

  const { data: produtosBusca = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-kit-busca', buscaProduto],
    queryFn: async () => {
      if (buscaProduto.length < 2) return []
      return (await api.get('/cadastros/produtos', { params: { q: buscaProduto } })).data
    },
    enabled: buscaProduto.length >= 2,
  })

  const { data: sugestao } = useQuery<any>({
    queryKey: ['kit-sugestao', produtoId, margemSugestao],
    queryFn: async () =>
      (await api.get(`/cadastros/produtos/${produtoId}/kit-sugestao-preco`, { params: { margem: margemSugestao } })).data,
    enabled: componentes.length > 0,
  })

  const addComp = useMutation({
    mutationFn: async () => {
      if (!novoComp.produto_id) throw new Error('Escolha um produto')
      return api.post(`/cadastros/produtos/${produtoId}/kit-componentes`, {
        componente_produto_id: novoComp.produto_id,
        quantidade: novoComp.qtd,
        percentage_share: novoComp.pct,
        ordem: componentes.length,
      })
    },
    onSuccess: () => {
      toast.success('Componente adicionado')
      qc.invalidateQueries({ queryKey: ['kit-comps', produtoId] })
      setNovoComp({ qtd: 1, pct: 0 })
      setBuscaProduto('')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const delComp = useMutation({
    mutationFn: async (cid: number) =>
      api.delete(`/cadastros/produtos/${produtoId}/kit-componentes/${cid}`),
    onSuccess: () => {
      toast.success('Componente removido')
      qc.invalidateQueries({ queryKey: ['kit-comps', produtoId] })
    },
  })

  const somaPct = useMemo(() =>
    componentes.reduce((acc, c) => acc + Number(c.percentage_share || 0), 0),
    [componentes])

  // Quantos kits completos dá pra montar com o estoque desta loja: é o componente
  // mais escasso que manda (tem 10 selins e 2 guidões, kit leva 1 de cada → 2 kits).
  const montagem = useMemo(() => {
    const possiveis = componentes
      .filter(c => Number(c.quantidade) > 0)
      .map(c => ({ c, kits: Math.floor(Number(c.componente_estoque || 0) / Number(c.quantidade)) }))
    if (!possiveis.length) return null
    const menor = possiveis.reduce((a, b) => (b.kits < a.kits ? b : a))
    return { kits: Math.max(0, menor.kits), limitante: menor.c }
  }, [componentes])

  return (
    <div className="p-5 space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        🎁 <strong>Componentes do kit</strong>: ao vender este kit, o sistema baixa cada componente
        individualmente do estoque e lista todos na NF-e. A % rateio define quanto do preço total é
        alocado em cada componente.
      </div>

      {/* Lista componentes */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Código</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Produto</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Qtd</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Estoque</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">%</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Custo unit</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Subtotal</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={7} className="text-center py-4 text-ink-400">Carregando...</td></tr>}
            {!isLoading && componentes.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-ink-400">Nenhum componente</td></tr>
            )}
            {componentes.map(c => {
              const custoTotal = Number(c.componente_preco_custo || 0) * Number(c.quantidade)
              return (
                <tr key={c.id}>
                  <td className="px-3 py-2 font-mono text-xs">{c.componente_codigo}</td>
                  <td className="px-3 py-2">{c.componente_descricao}</td>
                  <td className="px-3 py-2 text-right font-mono">{Number(c.quantidade)} {c.componente_unidade}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {(() => {
                      const est = Number(c.componente_estoque || 0)
                      const consol = Number(c.componente_estoque_consolidado ?? est)
                      // Vermelho = não dá nem 1 kit com o que tem nesta loja
                      const falta = est < Number(c.quantidade)
                      return (
                        <span title={consol > est ? `${fmtEstoque(consol)} no total da empresa` : undefined}
                          className={falta ? 'text-red-600 font-semibold' : ''}>
                          {fmtEstoque(est)} {c.componente_unidade}
                          {consol > est && (
                            <span className="block text-[10px] text-sky-700 font-normal">
                              {fmtEstoque(consol)} no total
                            </span>
                          )}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{Number(c.percentage_share || 0).toFixed(2)}%</td>
                  <td className="px-3 py-2 text-right">{formatMoney(c.componente_preco_custo)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatMoney(custoTotal)}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => pedirExclusao({ descricao: `o componente "${c.componente_descricao}"`, onConfirm: () => delComp.mutate(c.id) })}
                      className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {componentes.length > 0 && (
        <div className={`flex items-center justify-between rounded-lg p-3 text-sm ${
          Math.abs(somaPct - 100) < 0.01 ? 'bg-emerald-50 text-emerald-800'
          : somaPct === 0 ? 'bg-ink-100 text-ink-600'
          : 'bg-red-50 text-red-800'
        }`}>
          <span>Soma das % rateio: <strong>{somaPct.toFixed(2)}%</strong></span>
          {Math.abs(somaPct - 100) < 0.01 && <span>✓ OK</span>}
          {somaPct > 0 && Math.abs(somaPct - 100) >= 0.01 && <span>⚠ Deve somar 100%</span>}
          {somaPct === 0 && <span className="text-xs">(sem rateio → distribui por preço)</span>}
        </div>
      )}

      {montagem && (
        <div className={`flex items-center justify-between rounded-lg p-3 text-sm border ${
          montagem.kits > 0 ? 'border-emerald-300 text-emerald-800' : 'border-red-300 text-red-700'}`}>
          <span>
            Com o estoque desta loja dá pra montar <strong>{montagem.kits}</strong> kit(s)
          </span>
          {montagem.kits === 0 && (
            <span className="text-xs">
              Falta: <strong>{montagem.limitante.componente_descricao}</strong>
            </span>
          )}
        </div>
      )}

      {/* Sugestão de preço.
          FIX: o bloco era um GRADIENTE (from-brand-50 to-brand-100), que o tema
          escuro não converte — ficava claro, enquanto os valores sem cor explícita
          herdavam o branco do tema: texto branco sobre fundo branco. Agora é card
          normal (o tema converte) com cor explícita em todo texto. */}
      {sugestao && (
        <div className="bg-white border border-brand-300 rounded-lg p-4 border-l-4 border-l-brand-600">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={16} className="text-brand-600" />
            <h4 className="font-semibold text-ink-900">Sugestão de preço</h4>
          </div>
          <div className="grid grid-cols-4 gap-3 text-sm items-end">
            <div>
              <div className="text-xs text-ink-500">Custo total</div>
              <div className="font-bold text-ink-900">{formatMoney(sugestao.custo_total)}</div>
            </div>
            <div>
              <div className="text-xs text-ink-500">Venda componentes</div>
              <div className="font-bold text-ink-900">{formatMoney(sugestao.venda_total)}</div>
            </div>
            <div>
              <div className="text-xs text-ink-500">Margem %</div>
              <input type="number" value={margemSugestao}
                onChange={e => setMargemSugestao(parseFloat(e.target.value) || 0)}
                className="input w-20 py-1 text-sm" />
            </div>
            <div>
              <div className="text-xs text-ink-500">Sugestão</div>
              {/* Fundo sólido + texto branco: legível nos dois temas */}
              <div className="inline-block mt-0.5 px-2.5 py-1 rounded-md bg-brand-600 text-white font-bold text-lg">
                {formatMoney(sugestao.sugestao)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adicionar componente */}
      <div className="border border-ink-200 rounded-lg p-4 space-y-3">
        <h4 className="font-semibold text-sm">+ Adicionar componente</h4>
        <div className="relative">
          <input className="input" placeholder="Buscar produto por código ou descrição..."
            value={buscaProduto} onChange={e => setBuscaProduto(e.target.value)} />
          {buscaProduto.length >= 2 && produtosBusca.length > 0 && !novoComp.produto_id && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-ink-200 rounded-lg shadow-lg max-h-48 overflow-auto">
              {produtosBusca.filter(p => p.tipo !== 'kit' && p.tipo !== 'grade' && p.id !== produtoId).map(p => (
                <button key={p.id}
                  onClick={() => {
                    setNovoComp(v => ({ ...v, produto_id: p.id }))
                    setBuscaProduto(`${p.codigo} — ${p.descricao}`)
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-ink-50 text-sm">
                  <span className="font-mono text-xs text-ink-500 mr-2">{p.codigo}</span>
                  {p.descricao}
                  <span className="ml-2 text-xs text-ink-400">{formatMoney(p.preco_custo)}</span>
                  <span className={`ml-2 text-xs ${Number(p.estoque_atual || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    · est. {fmtEstoque(p.estoque_atual)} {p.unidade}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label">Quantidade</label>
            <input className="input" type="number" step="0.001" value={novoComp.qtd}
              onChange={e => setNovoComp(v => ({ ...v, qtd: parseFloat(e.target.value) || 1 }))} />
          </div>
          <div>
            <label className="label">% rateio</label>
            <input className="input" type="number" step="0.01" value={novoComp.pct}
              onChange={e => setNovoComp(v => ({ ...v, pct: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div className="flex items-end">
            <button onClick={() => addComp.mutate()} disabled={!novoComp.produto_id || addComp.isPending}
              className="btn-primary w-full">
              {addComp.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Adicionar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


// ============ TAB KIT LOCAL (produto novo, ainda sem id) ============
// Mesma seleção de componentes da TabKit, mas guardada em estado local e gravada
// junto com o produto no Salvar (o produto novo ainda não tem id pra FK).
function TabKitLocal({ comps, setComps, onSalvar, saving, canSave }: {
  comps: any[]
  setComps: (fn: (prev: any[]) => any[]) => void
  onSalvar: () => void
  saving: boolean
  canSave: boolean
}) {
  const [buscaProduto, setBuscaProduto] = useState('')
  const [novo, setNovo] = useState<{ produto?: Produto; qtd: number; pct: number }>({ qtd: 1, pct: 0 })

  const { data: produtosBusca = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-kit-busca-local', buscaProduto],
    queryFn: async () => {
      if (buscaProduto.length < 2) return []
      return (await api.get('/cadastros/produtos', { params: { q: buscaProduto } })).data
    },
    enabled: buscaProduto.length >= 2,
  })

  const add = () => {
    if (!novo.produto) return
    if (comps.some(c => c.componente_produto_id === novo.produto!.id)) {
      toast.error('Componente já adicionado'); return
    }
    const p = novo.produto
    setComps(prev => [...prev, {
      componente_produto_id: p.id,
      componente_codigo: p.codigo,
      componente_descricao: p.descricao,
      componente_preco_custo: p.preco_custo,
      componente_preco_venda: p.preco_venda,
      componente_unidade: p.unidade,
      componente_estoque: p.estoque_atual,
      quantidade: novo.qtd,
      percentage_share: novo.pct,
    }])
    setNovo({ qtd: 1, pct: 0 }); setBuscaProduto('')
  }
  const remover = (id: number) => setComps(prev => prev.filter(c => c.componente_produto_id !== id))
  const somaPct = comps.reduce((a, c) => a + Number(c.percentage_share || 0), 0)

  return (
    <div className="p-5 space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        🎁 Monte o kit escolhendo produtos <strong>já cadastrados</strong>. Ao clicar em <strong>Salvar produto</strong>,
        o kit e seus componentes são gravados juntos. A % rateio define quanto do preço total vai pra cada item.
      </div>

      {/* Lista local */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Código</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Produto</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Qtd</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">%</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Custo unit</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Estoque</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {comps.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-ink-400">Nenhum componente — busque e adicione abaixo</td></tr>
            )}
            {comps.map(c => (
              <tr key={c.componente_produto_id}>
                <td className="px-3 py-2 font-mono text-xs">{c.componente_codigo}</td>
                <td className="px-3 py-2">{c.componente_descricao}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(c.quantidade)} {c.componente_unidade}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(c.percentage_share || 0).toFixed(2)}%</td>
                <td className="px-3 py-2 text-right">{formatMoney(c.componente_preco_custo)}</td>
                <td className={`px-3 py-2 text-right font-mono ${
                  Number(c.componente_estoque || 0) < Number(c.quantidade) ? 'text-red-600 font-semibold' : ''}`}>
                  {fmtEstoque(c.componente_estoque)} {c.componente_unidade}
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => remover(c.componente_produto_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {comps.length > 0 && (
        <div className={`flex items-center justify-between rounded-lg p-3 text-sm ${
          Math.abs(somaPct - 100) < 0.01 ? 'bg-emerald-50 text-emerald-800'
          : somaPct === 0 ? 'bg-ink-100 text-ink-600' : 'bg-red-50 text-red-800'}`}>
          <span>Soma das % rateio: <strong>{somaPct.toFixed(2)}%</strong></span>
          {somaPct === 0 && <span className="text-xs">(sem rateio → distribui por preço)</span>}
        </div>
      )}

      {/* Adicionar componente */}
      <div className="border border-ink-200 rounded-lg p-4 space-y-3">
        <h4 className="font-semibold text-sm">+ Adicionar componente</h4>
        <div className="relative">
          <input className="input" placeholder="Buscar produto por código ou descrição..."
            value={buscaProduto} onChange={e => { setBuscaProduto(e.target.value); setNovo(v => ({ ...v, produto: undefined })) }} />
          {buscaProduto.length >= 2 && produtosBusca.length > 0 && !novo.produto && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-ink-200 rounded-lg shadow-lg max-h-48 overflow-auto">
              {produtosBusca.filter(p => p.tipo !== 'kit' && p.tipo !== 'grade').map(p => (
                <button key={p.id}
                  onClick={() => { setNovo(v => ({ ...v, produto: p })); setBuscaProduto(`${p.codigo} — ${p.descricao}`) }}
                  className="w-full text-left px-3 py-2 hover:bg-ink-50 text-sm">
                  <span className="font-mono text-xs text-ink-500 mr-2">{p.codigo}</span>{p.descricao}
                  <span className="ml-2 text-xs text-ink-400">{formatMoney(p.preco_custo)}</span>
                  <span className={`ml-2 text-xs ${Number(p.estoque_atual || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    · est. {fmtEstoque(p.estoque_atual)} {p.unidade}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label">Quantidade</label>
            <input className="input" type="number" step="0.001" value={novo.qtd}
              onChange={e => setNovo(v => ({ ...v, qtd: parseFloat(e.target.value) || 1 }))} />
          </div>
          <div>
            <label className="label">% rateio</label>
            <input className="input" type="number" step="0.01" value={novo.pct}
              onChange={e => setNovo(v => ({ ...v, pct: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div className="flex items-end">
            <button onClick={add} disabled={!novo.produto} className="btn-primary w-full">
              <Plus size={14} /> Adicionar
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-1">
        <button onClick={onSalvar} disabled={!canSave || saving} className="btn-primary">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : <>Salvar produto{comps.length > 0 ? ` + ${comps.length} componente${comps.length > 1 ? 's' : ''}` : ''}</>}
        </button>
      </div>
    </div>
  )
}


// ============ TAB UNIDADES (conversões) ============
interface GradeValorComTipo {
  id: number; tipo_id: number; codigo: string; label: string
  valor_numerico: number | null; hex_cor: string | null; ordem: number; ativo: boolean
  tipo_codigo: string; tipo_label: string
}

function TabBorracha({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()

  const { data: produto, isLoading: lp } = useQuery<any>({
    queryKey: ['produto', produtoId],
    queryFn: async () => (await api.get(`/cadastros/produtos/${produtoId}`)).data,
  })

  const [geo, setGeo] = useState({ peso_liquido: '', densidade_g_cm3: '', espessura_mm: '', largura_mm: '', comprimento_mm: '' })
  useEffect(() => {
    if (produto) setGeo({
      peso_liquido: produto.peso_liquido ?? '',
      densidade_g_cm3: produto.densidade_g_cm3 ?? '',
      espessura_mm: produto.espessura_mm ?? '',
      largura_mm: produto.largura_mm ?? '',
      comprimento_mm: produto.comprimento_mm ?? '',
    })
  }, [produto])

  const salvarGeo = useMutation({
    mutationFn: async () => {
      const payload = { ...produto }
      ;(['peso_liquido','densidade_g_cm3','espessura_mm','largura_mm','comprimento_mm'] as const).forEach(k => {
        const v = (geo as any)[k]
        payload[k] = v === '' || v == null ? null : Number(v)
      })
      return (await api.put(`/cadastros/produtos/${produtoId}`, payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['produto', produtoId] })
      toast.success('Geometria salva')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const pesoCalc = useMemo(() => {
    const pl = Number(geo.peso_liquido)
    if (pl > 0) return pl
    const d = Number(geo.densidade_g_cm3)
    const e = Number(geo.espessura_mm)
    const l = Number(geo.largura_mm)
    const c = Number(geo.comprimento_mm)
    if (d > 0 && e > 0 && l > 0 && c > 0) return (d * e * l * c) / 1_000_000
    return 0
  }, [geo])

  const { data: vinculados = [], isLoading: lv } = useQuery<GradeValorComTipo[]>({
    queryKey: ['produto-grades', produtoId],
    queryFn: async () => (await api.get(`/cadastros/produtos/${produtoId}/grades`)).data,
  })

  const { data: todos = [], isLoading: lt } = useQuery<GradeValorComTipo[]>({
    queryKey: ['grade-valores-todos'],
    queryFn: async () => (await api.get('/cadastros/grade-valores')).data,
  })

  const salvarVinculos = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.put(`/cadastros/produtos/${produtoId}/grades`, { grade_valor_ids: ids })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['produto-grades', produtoId] })
      toast.success('Vínculos atualizados')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const idsVinc = useMemo(() => new Set(vinculados.map(v => v.id)), [vinculados])
  const porTipo = useMemo(() => {
    const m: Record<string, { tipo_label: string; valores: GradeValorComTipo[] }> = {}
    todos.forEach(v => {
      const k = v.tipo_codigo
      if (!m[k]) m[k] = { tipo_label: v.tipo_label, valores: [] }
      m[k].valores.push(v)
    })
    return m
  }, [todos])

  const toggle = (vid: number) => {
    const next = new Set(idsVinc)
    if (next.has(vid)) next.delete(vid); else next.add(vid)
    salvarVinculos.mutate(Array.from(next))
  }

  if (lv || lt || lp) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>

  const geoFields = (
    <div className="border rounded-lg p-3 space-y-3">
      <div className="font-semibold text-ink-900">Geometria & peso</div>
      <div className="text-xs text-ink-500">
        Auto-OP usa <code>peso_liquido</code> se &gt; 0, senão calcula geometricamente:
        densidade × espessura × largura × comprimento ÷ 1.000.000.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {(
          [
            ['peso_liquido', 'Peso líquido (kg)'],
            ['densidade_g_cm3', 'Densidade (g/cm³)'],
            ['espessura_mm', 'Espessura (mm)'],
            ['largura_mm', 'Largura (mm)'],
            ['comprimento_mm', 'Comprimento (mm)'],
          ] as const
        ).map(([k, label]) => (
          <label key={k} className="text-xs text-ink-600 flex flex-col gap-1">
            {label}
            <input
              type="number"
              step="0.0001"
              value={(geo as any)[k]}
              onChange={e => setGeo({ ...geo, [k]: e.target.value })}
              className="border rounded px-2 py-1 text-sm"
            />
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="text-sm text-ink-700">
          Peso unitário calculado: <strong>{pesoCalc.toFixed(4)} kg</strong>
        </div>
        <button type="button"
          onClick={() => salvarGeo.mutate()}
          disabled={salvarGeo.isPending}
          className="px-3 py-1.5 text-sm rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
          Salvar geometria
        </button>
      </div>
    </div>
  )

  return (
    <div className="p-5 space-y-4">
      {geoFields}
      {Object.keys(porTipo).length === 0 ? (
        <div className="border rounded-lg p-3 text-sm text-ink-500">
          Nenhuma grade cadastrada. Vá em <strong>Cadastros → Grades</strong> pra cadastrar tipos (cor, espessura, mm) e seus valores.
        </div>
      ) : (
        <>
      <div className="text-sm text-ink-600">
        Vincule atributos de grade a este produto. Em borracha, a <strong>cor</strong> é usada pela auto-OP pra escolher a receita correta na ficha técnica.
      </div>
      {Object.entries(porTipo).map(([codigo, grupo]) => (
        <div key={codigo} className="border rounded-lg p-3">
          <div className="font-semibold text-ink-900 mb-2">{grupo.tipo_label}</div>
          <div className="flex flex-wrap gap-2">
            {grupo.valores.map(v => {
              const ativo = idsVinc.has(v.id)
              return (
                <button key={v.id}
                  type="button"
                  onClick={() => toggle(v.id)}
                  disabled={salvarVinculos.isPending}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition ${ativo ? 'bg-brand-50 border-brand-400 text-brand-700' : 'bg-white border-ink-200 text-ink-600 hover:border-ink-300'}`}>
                  {v.hex_cor && (
                    <span className="w-4 h-4 rounded-full border" style={{ background: v.hex_cor }} />
                  )}
                  {v.label}
                  {v.valor_numerico != null && <span className="text-ink-400">({v.valor_numerico})</span>}
                </button>
              )
            })}
          </div>
        </div>
      ))}
        </>
      )}
    </div>
  )
}


function TabFornecedores({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()
  const [novo, setNovo] = useState<any>({ fornecedor_id: '', codigo_fornecedor: '', ean_fornecedor: '', fator_conversao: 1, lead_time_dias: 0 })

  const { data: vinculos = [], isLoading } = useQuery<any[]>({
    queryKey: ['prod-fornecedores', produtoId],
    queryFn: async () => (await api.get(`/compras/produtos/${produtoId}/fornecedores`)).data,
  })
  const { data: fornecedores = [] } = useQuery<any[]>({
    queryKey: ['fornecedores-lista'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor', limit: 1000 } })).data,
  })

  const add = useMutation({
    mutationFn: async () => api.post(`/compras/produtos/${produtoId}/fornecedores`, {
      ...novo, fornecedor_id: Number(novo.fornecedor_id),
      fator_conversao: Number(novo.fator_conversao || 1), lead_time_dias: Number(novo.lead_time_dias || 0),
    }),
    onSuccess: () => {
      toast.success('Fornecedor vinculado')
      qc.invalidateQueries({ queryKey: ['prod-fornecedores', produtoId] })
      setNovo({ fornecedor_id: '', codigo_fornecedor: '', ean_fornecedor: '', fator_conversao: 1, lead_time_dias: 0 })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/compras/produtos/fornecedores/${id}`),
    onSuccess: () => { toast.success('Removido'); qc.invalidateQueries({ queryKey: ['prod-fornecedores', produtoId] }) },
  })

  return (
    <div className="p-5 space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        🚚 <strong>Códigos do fornecedor (de-para)</strong>: cadastre o código que cada fornecedor usa
        pra ESTE produto. Ao importar o XML da NF, o item entra <strong>vinculado automaticamente</strong>.
        O mesmo produto pode ter vários fornecedores/códigos. Use o <strong>fator</strong> quando a embalagem
        do fornecedor difere da sua (ex.: ele manda CX de 12, você estoca UN → fator 12).
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Fornecedor</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Cód. forn.</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">EAN</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Fator</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Lead (d)</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={6} className="text-center py-4 text-ink-400">Carregando...</td></tr>}
            {!isLoading && vinculos.length === 0 && (
              <tr><td colSpan={6} className="text-center py-4 text-ink-400">Nenhum fornecedor vinculado</td></tr>
            )}
            {vinculos.map(v => (
              <tr key={v.id}>
                <td className="px-3 py-2">{v.fornecedor_nome || `#${v.fornecedor_id}`}</td>
                <td className="px-3 py-2 font-mono text-xs">{v.codigo_fornecedor || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs text-ink-500">{v.ean_fornecedor || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">× {Number(v.fator_conversao)}</td>
                <td className="px-3 py-2 text-right">{v.lead_time_dias || 0}</td>
                <td className="px-3 py-2">
                  <button onClick={() => pedirExclusao({ descricao: `o fornecedor "${v.fornecedor_nome || `#${v.fornecedor_id}`}"`, onConfirm: () => del.mutate(v.id) })} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-6 gap-2 items-end">
        <div className="col-span-2">
          <label className="text-xs text-ink-500">Fornecedor</label>
          <select className="input !py-1 text-sm" value={novo.fornecedor_id}
            onChange={e => setNovo({ ...novo, fornecedor_id: e.target.value })}>
            <option value="">— selecione —</option>
            {fornecedores.map((f: any) => <option key={f.id} value={f.id}>{f.codigo ? `${f.codigo} - ` : ''}{f.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-ink-500">Cód. forn.</label>
          <input className="input !py-1 text-sm" value={novo.codigo_fornecedor}
            onChange={e => setNovo({ ...novo, codigo_fornecedor: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-ink-500">EAN</label>
          <input className="input !py-1 text-sm" value={novo.ean_fornecedor}
            onChange={e => setNovo({ ...novo, ean_fornecedor: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-ink-500">Fator</label>
          <input type="number" step="0.0001" className="input !py-1 text-sm" value={novo.fator_conversao}
            onChange={e => setNovo({ ...novo, fator_conversao: e.target.value })} />
        </div>
        <button onClick={() => add.mutate()} disabled={!novo.fornecedor_id || add.isPending}
          className="btn-primary !py-1.5 text-sm disabled:opacity-50">Adicionar</button>
      </div>
    </div>
  )
}

function TabUnidades({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()
  const [novo, setNovo] = useState<any>({ codigo: '', unidade: 'CX', fator: 1, preco_venda: 0, ativo: true })

  const { data: unidades = [], isLoading } = useQuery<UnidadeConv[]>({
    queryKey: ['unidades-conv', produtoId],
    queryFn: async () => (await api.get(`/cadastros/produtos/${produtoId}/unidades-conversao`)).data,
  })

  const addUnidade = useMutation({
    mutationFn: async () => api.post(`/cadastros/produtos/${produtoId}/unidades-conversao`, novo),
    onSuccess: () => {
      toast.success('Unidade adicionada')
      qc.invalidateQueries({ queryKey: ['unidades-conv', produtoId] })
      setNovo({ codigo: '', unidade: 'CX', fator: 1, preco_venda: 0, ativo: true })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const delUnidade = useMutation({
    mutationFn: async (uid: number) =>
      api.delete(`/cadastros/produtos/${produtoId}/unidades-conversao/${uid}`),
    onSuccess: () => {
      toast.success('Removida')
      qc.invalidateQueries({ queryKey: ['unidades-conv', produtoId] })
    },
  })

  return (
    <div className="p-5 space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        📦 <strong>Unidades alternativas</strong>: mesmo produto vendido em embalagens diferentes.
        Ex: Parafuso vendido também em CAIXA de 100. O estoque é único, só diferencia na venda.
        Cada unidade pode ter seu SKU no ML.
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">SKU alternativo</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">EAN</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Unid.</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Fator</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Preço</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">ML</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={7} className="text-center py-4 text-ink-400">Carregando...</td></tr>}
            {!isLoading && unidades.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-ink-400">Nenhuma unidade alternativa</td></tr>
            )}
            {unidades.map(u => (
              <tr key={u.id}>
                <td className="px-3 py-2 font-mono text-xs">{u.codigo}</td>
                <td className="px-3 py-2 font-mono text-xs text-ink-500">{u.ean || '—'}</td>
                <td className="px-3 py-2 text-center font-medium">{u.unidade}</td>
                <td className="px-3 py-2 text-right font-mono">× {Number(u.fator)}</td>
                <td className="px-3 py-2 text-right">{u.preco_venda ? formatMoney(u.preco_venda) : '—'}</td>
                <td className="px-3 py-2 font-mono text-xs text-ink-500">{u.ml_item_id || '—'}</td>
                <td className="px-3 py-2">
                  <button onClick={() => pedirExclusao({ descricao: `a unidade "${u.unidade}"`, onConfirm: () => delUnidade.mutate(u.id) })}
                    className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-ink-200 rounded-lg p-4 space-y-3">
        <h4 className="font-semibold text-sm">+ Adicionar unidade alternativa</h4>
        <div className="grid grid-cols-4 gap-2">
          <div className="col-span-2">
            <label className="label">Código SKU</label>
            <input className="input" value={novo.codigo}
              onChange={e => setNovo((v: any) => ({ ...v, codigo: e.target.value }))}
              placeholder="PARAFUSO-CX100" />
          </div>
          <div>
            <label className="label">Unidade</label>
            <input className="input" value={novo.unidade}
              onChange={e => setNovo((v: any) => ({ ...v, unidade: e.target.value.toUpperCase() }))} />
          </div>
          <div>
            <label className="label">Fator</label>
            <input className="input" type="number" step="0.0001" value={novo.fator}
              onChange={e => setNovo((v: any) => ({ ...v, fator: parseFloat(e.target.value) || 1 }))} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label">EAN (opcional)</label>
            <input className="input" value={novo.ean || ''}
              onChange={e => setNovo((v: any) => ({ ...v, ean: e.target.value }))} />
          </div>
          <div>
            <label className="label">Preço venda</label>
            <input className="input" type="number" step="0.01" value={novo.preco_venda ?? ''}
              onChange={e => setNovo((v: any) => ({ ...v, preco_venda: parseFloat(e.target.value) || null }))} />
          </div>
          <div>
            <label className="label">Descrição</label>
            <input className="input" value={novo.descricao_alt || ''}
              onChange={e => setNovo((v: any) => ({ ...v, descricao_alt: e.target.value }))}
              placeholder="Caixa com 100" />
          </div>
        </div>
        <button onClick={() => addUnidade.mutate()}
          disabled={!novo.codigo || !novo.unidade || addUnidade.isPending}
          className="btn-primary">
          {addUnidade.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Adicionar
        </button>
      </div>
    </div>
  )
}

// ============ TAB MARKETPLACE ============
function TabMarketplace({ editing, setEditing }: any) {
  const set = (k: string, v: any) => setEditing((p: any) => ({ ...p, [k]: v }))
  return (
    <div className="p-5 space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        🛒 Vínculos com anúncios do Mercado Livre. Quando o ML chamar o webhook com um pedido,
        o sistema procura pelo <code className="bg-blue-100 px-1">ml_item_id</code> + <code className="bg-blue-100 px-1">ml_variation_id</code>
        para identificar este produto. Use "Unidades alternativas" se o anúncio ML vende em embalagem diferente.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">ID do anúncio (MLB…)</label>
          <input className="input font-mono" value={editing.ml_item_id || ''}
            onChange={e => set('ml_item_id', e.target.value)} placeholder="MLB3737188005" />
        </div>
        <div>
          <label className="label">ID da variação</label>
          <input className="input font-mono" value={editing.ml_variation_id || ''}
            onChange={e => set('ml_variation_id', e.target.value)} placeholder="94754627308" />
        </div>
      </div>
      <div>
        <label className="label">Tipo do anúncio</label>
        <select className="input" value={editing.ml_anuncio_tipo || ''} onChange={e => set('ml_anuncio_tipo', e.target.value)}>
          <option value="">— não é anúncio ML —</option>
          <option value="single">single (produto unitário)</option>
          <option value="variation">variation (produto com variações)</option>
          <option value="bundle">bundle (kit)</option>
        </select>
      </div>
    </div>
  )
}

// ============ TAB GALERIA ============
function TabGaleria({ produtoId }: { produtoId: number }) {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const { data: fotos = [], isLoading } = useQuery<ProdutoFoto[]>({
    queryKey: ['fotos', produtoId],
    queryFn: async () => (await api.get(`/cadastros/produtos/${produtoId}/fotos`)).data,
  })

  const delFoto = useMutation({
    mutationFn: async (fid: number) =>
      api.delete(`/cadastros/produtos/${produtoId}/fotos/${fid}`),
    onSuccess: () => {
      toast.success('Foto removida')
      qc.invalidateQueries({ queryKey: ['fotos', produtoId] })
    },
  })

  const setPrincipal = useMutation({
    mutationFn: async (fid: number) =>
      api.put(`/cadastros/produtos/${produtoId}/fotos/${fid}/principal`),
    onSuccess: () => {
      toast.success('Foto principal atualizada')
      qc.invalidateQueries({ queryKey: ['fotos', produtoId] })
    },
  })

  async function uploadFiles(files: FileList | File[]) {
    const arr = Array.from(files)
    if (arr.length === 0) return

    const validas = arr.filter(f => f.type.startsWith('image/'))
    if (validas.length === 0) {
      toast.error('Apenas imagens JPG, PNG ou WebP')
      return
    }
    if (validas.length + fotos.length > 10) {
      toast.error('Máximo 10 fotos por produto')
      return
    }

    setUploading(true)
    try {
      for (const file of validas) {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('principal', fotos.length === 0 ? 'true' : 'false')

        const token = localStorage.getItem('pain_token')
        const r = await fetch(`/api/cadastros/produtos/${produtoId}/fotos`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        })
        if (!r.ok) {
          const err = await r.json().catch(() => ({ detail: r.statusText }))
          throw new Error(err.detail || `HTTP ${r.status}`)
        }
      }
      toast.success(`${validas.length} foto(s) enviada(s)`)
      qc.invalidateQueries({ queryKey: ['fotos', produtoId] })
    } catch (e: any) {
      toast.error(e.message || 'Erro no upload')
    } finally {
      setUploading(false)
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) uploadFiles(e.target.files)
    e.target.value = ''
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files)
  }

  return (
    <div className="p-5 space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        📷 <strong>Galeria</strong>: até 10 fotos por produto. Formatos: JPG, PNG, WebP (máx 10MB).
        A foto <strong>principal</strong> aparece na listagem e no PDV. Arraste fotos aqui ou clique para selecionar.
      </div>

      {/* Dropzone */}
      <label
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-brand-500 bg-brand-50' : 'border-ink-300 hover:border-ink-400 bg-ink-50/50'
        }`}>
        <input type="file" multiple accept="image/*" className="hidden"
          onChange={onFileInput} disabled={uploading || fotos.length >= 10} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-ink-600">
            <Loader2 className="animate-spin" size={18} /> Enviando...
          </div>
        ) : (
          <>
            <Upload size={28} className="mx-auto text-ink-400 mb-2" />
            <p className="text-sm text-ink-600">
              <strong>Clique</strong> ou arraste fotos aqui
            </p>
            <p className="text-xs text-ink-400 mt-1">
              {fotos.length}/10 fotos · JPG/PNG/WebP até 10MB
            </p>
          </>
        )}
      </label>

      {/* Grid de fotos */}
      {isLoading ? (
        <div className="text-center py-6 text-ink-400">
          <Loader2 className="animate-spin inline mr-2" /> Carregando...
        </div>
      ) : fotos.length === 0 ? (
        <div className="text-center py-10 text-ink-400 text-sm">
          Nenhuma foto ainda. Adicione a primeira acima.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {fotos.map(f => (
            <div key={f.id} className={`relative group rounded-lg overflow-hidden border-2 ${
              f.principal ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-ink-200'
            }`}>
              <img src={f.url_medium} alt="" className="w-full h-40 object-cover bg-ink-100" />
              {f.principal && (
                <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <Star size={10} fill="currentColor" /> PRINCIPAL
                </div>
              )}
              {/* overlay com ações */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!f.principal && (
                  <button onClick={() => setPrincipal.mutate(f.id)}
                    title="Marcar como principal"
                    className="bg-white text-ink-900 p-2 rounded hover:bg-brand-100">
                    <Star size={16} />
                  </button>
                )}
                <a href={f.url_full} target="_blank" rel="noreferrer"
                  title="Ver em tamanho real"
                  className="bg-white text-ink-900 p-2 rounded hover:bg-ink-100">
                  <ImageIcon size={16} />
                </a>
                <button onClick={() => pedirExclusao({ descricao: 'esta foto', onConfirm: () => delFoto.mutate(f.id) })}
                  title="Remover"
                  className="bg-red-600 text-white p-2 rounded hover:bg-red-700">
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-2 py-1 flex justify-between">
                <span>{f.largura}×{f.altura}</span>
                <span>{((f.tamanho_bytes || 0) / 1024).toFixed(0)} KB</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============ ETIQUETAS MODAL (Pimaco 6181) ============
interface EtiqItem { produto_id: number; quantidade: number }

function EtiquetasModal({ onClose }: { onClose: () => void }) {
  const [itens, setItens] = useState<EtiqItem[]>([])
  const [incluirQr, setIncluirQr] = useState(true)
  const [busca, setBusca] = useState('')
  const [gerando, setGerando] = useState(false)
  // produtos já resolvidos (da busca ou adicionados) — pra exibir a descrição dos selecionados
  const [resolved, setResolved] = useState<Record<number, Produto>>({})

  // busca server-side (cobre todos os produtos, não só a página atual)
  const { data: resultados = [] } = useQuery<Produto[]>({
    queryKey: ['etiquetas-busca', busca],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: busca || undefined, limit: 50 } })).data,
    enabled: busca.trim().length >= 1,
  })
  useEffect(() => {
    if (resultados.length) setResolved(prev => {
      const m = { ...prev }
      resultados.forEach(p => { if (p.id) m[p.id] = p })
      return m
    })
  }, [resultados])
  const prodMap = resolved

  function adicionar(p: Produto) {
    if (!p.id) return
    setResolved(prev => ({ ...prev, [p.id!]: p }))
    setItens(prev => {
      const idx = prev.findIndex(i => i.produto_id === p.id)
      if (idx >= 0) {
        const novo = [...prev]
        novo[idx] = { ...novo[idx], quantidade: novo[idx].quantidade + 1 }
        return novo
      }
      return [...prev, { produto_id: p.id!, quantidade: 1 }]
    })
  }

  async function adicionarPorCodigoOuEan() {
    const term = busca.trim()
    if (!term) return
    const lista: Produto[] = (await api.get('/cadastros/produtos', { params: { q: term, limit: 50 } })).data
    const tl = term.toLowerCase()
    const achado = lista.find(p =>
      (p.codigo || '').toLowerCase() === tl ||
      (p.ean || '').toLowerCase() === tl
    ) || lista.find(p =>
      (p.codigo || '').toLowerCase().includes(tl) ||
      (p.descricao || '').toLowerCase().includes(tl)
    )
    if (!achado) {
      toast.error(`Nao achei produto com codigo/EAN "${busca}"`)
      return
    }
    adicionar(achado)
    setBusca('')
  }

  function setQtd(produto_id: number, q: number) {
    setItens(prev => prev.map(i =>
      i.produto_id === produto_id ? { ...i, quantidade: Math.max(1, q) } : i
    ))
  }

  function remover(produto_id: number) {
    setItens(prev => prev.filter(i => i.produto_id !== produto_id))
  }

  async function gerar() {
    if (itens.length === 0) {
      toast.error('Adicione ao menos 1 produto')
      return
    }
    setGerando(true)
    try {
      const r = await api.post('/cadastros/produtos/etiquetas-pdf', {
        itens,
        incluir_qr: incluirQr,
        formato: 'pimaco_6181',
      }, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar PDF')
    } finally {
      setGerando(false)
    }
  }

  const totalEtiquetas = itens.reduce((s, i) => s + i.quantidade, 0)
  const folhas = Math.ceil(totalEtiquetas / 8)

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Tag size={18} /> Etiquetas de Produto</h3>
            <p className="text-xs text-ink-500 mt-0.5">Pimaco 6181 · A4 · 8 etiquetas por folha</p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-auto flex-1">
          {/* Busca rapida */}
          <div>
            <label className="label">Adicionar por codigo ou EAN</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  className="input pl-9"
                  placeholder="Digite SKU ou EAN e tecle Enter"
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarPorCodigoOuEan() } }}
                  autoFocus
                />
              </div>
              <button className="btn-ghost text-sm" onClick={adicionarPorCodigoOuEan}>
                <Plus size={14} /> Adicionar
              </button>
            </div>
          </div>

          {/* Lista de selecionados */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label m-0">Selecionados ({itens.length} produto{itens.length !== 1 ? 's' : ''})</label>
              {itens.length > 0 && (
                <button onClick={() => setItens([])} className="text-xs text-red-600 hover:underline">
                  Limpar tudo
                </button>
              )}
            </div>
            {itens.length === 0 ? (
              <div className="text-center py-8 text-ink-500 text-sm border-2 border-dashed border-ink-200 rounded">
                Nenhum produto selecionado. Use a busca acima ou clique em um produto na lista abaixo.
              </div>
            ) : (
              <div className="divide-y border border-ink-200 rounded max-h-48 overflow-auto">
                {itens.map(i => {
                  const p = prodMap[i.produto_id]
                  if (!p) return null
                  return (
                    <div key={i.produto_id} className="flex items-center gap-3 px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.descricao}</div>
                        <div className="text-xs text-ink-500 font-mono">{p.codigo} {p.ean ? `· EAN ${p.ean}` : ''}</div>
                      </div>
                      <input
                        type="number"
                        min={1}
                        className="input input-sm w-20 text-right"
                        value={i.quantidade}
                        onChange={e => setQtd(i.produto_id, parseInt(e.target.value) || 1)}
                      />
                      <button onClick={() => remover(i.produto_id)} className="text-ink-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Resultados da busca (clique pra adicionar) */}
          <div>
            <label className="label">Resultados da busca:</label>
            {busca.trim().length === 0 ? (
              <p className="text-xs text-ink-400 py-2">Digite acima (código, EAN ou nome) pra buscar produtos.</p>
            ) : resultados.filter(p => p.id).length === 0 ? (
              <p className="text-xs text-ink-400 py-2">Nenhum produto encontrado.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-auto p-1">
                {resultados.filter(p => p.id).map(p => (
                  <button
                    key={p.id}
                    onClick={() => p.id && adicionar(p)}
                    className="text-left border border-ink-200 rounded px-2 py-1.5 hover:bg-brand-50 hover:border-brand-300 text-xs transition"
                    title={`${p.descricao} — ${formatMoney(p.preco_venda)}`}
                  >
                    <div className="font-mono text-ink-700">{p.codigo}</div>
                    <div className="truncate text-ink-500">{p.descricao}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Opcoes */}
          <div className="border-t pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={incluirQr}
                onChange={e => setIncluirQr(e.target.checked)}
              />
              <QrCode size={14} />
              Incluir QR Code (scanner abre o produto no ERP)
            </label>
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-ink-50 flex items-center justify-between">
          <div className="text-xs text-ink-600">
            {totalEtiquetas > 0 ? (
              <>Total: <b>{totalEtiquetas}</b> etiqueta{totalEtiquetas !== 1 ? 's' : ''} · <b>{folhas}</b> folha{folhas !== 1 ? 's' : ''} A4</>
            ) : 'Adicione produtos'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
            <button onClick={gerar} disabled={gerando || itens.length === 0} className="btn-primary">
              {gerando ? <><Loader2 size={14} className="animate-spin" /> Gerando...</> : <><Printer size={14} /> Gerar PDF</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ ESTOQUE POR LOJA ============
// "Tem na outra loja?" é a pergunta que o vendedor faz com o cliente na frente.
// Antes ele precisava ligar pra outra unidade; agora vê aqui.
function EstoquePorLojaModal({ produto, onClose }: { produto: Produto; onClose: () => void }) {
  const { data: linhas = [], isLoading } = useQuery<any[]>({
    queryKey: ['saldos-depositos', produto.id],
    queryFn: async () => (await api.get(`/pdv/produto/${produto.id}/saldos-depositos`)).data,
  })
  const total = linhas.reduce((s, l) => s + Number(l.saldo || 0), 0)
  const fmt = (n: number) => Number(n).toFixed(3).replace(/\.?0+$/, '')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold flex items-center gap-2"><Store size={18} /> Estoque por loja</h3>
            <p className="text-xs text-ink-500 mt-0.5 truncate" title={produto.descricao}>
              {produto.codigo} · {produto.descricao}
            </p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="text-center py-6 text-ink-400">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Consultando...
            </div>
          ) : linhas.length === 0 ? (
            <p className="text-center py-6 text-ink-400 text-sm">Nenhum depósito cadastrado.</p>
          ) : (
            <div className="divide-y border border-ink-200 rounded">
              {linhas.map(l => {
                const saldo = Number(l.saldo || 0)
                return (
                  <div key={l.deposito_id}
                    className={`flex items-center justify-between px-3 py-2.5 ${
                      l.eh_minha_filial ? 'bg-brand-50' : ''}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{l.nome}</div>
                      {l.eh_minha_filial && (
                        <div className="text-[10px] text-brand-700 font-semibold">VOCÊ ESTÁ AQUI</div>
                      )}
                    </div>
                    <div className={`text-sm font-mono font-semibold shrink-0 ${
                      saldo > 0 ? 'text-ink-900' : 'text-ink-400'}`}>
                      {fmt(saldo)} {produto.unidade}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {!isLoading && linhas.length > 0 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t text-sm">
              <span className="text-ink-600">Total na empresa</span>
              <span className="font-mono font-bold">{fmt(total)} {produto.unidade}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
